import { useEffect, useState } from 'react';
import Col from 'react-bootstrap/esm/Col';
import Row from 'react-bootstrap/esm/Row';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/esm/Spinner';
import { useLocation, useParams } from 'react-router-dom';
import {
  addDocument,
  deleteDocument,
  getDocuments,
  openPdf,
} from '../../../api/Assets';
import { getCompetition } from '../../../api/Competitions';
import EmptyState from '../../../components/EmptyState';
import Loading from '../../../components/Loading';
import ModalCentered from '../../../components/ModalCentered';
import PageTitle from '../../../components/PageTitle';
import UploadButton from '../../../components/Buttons/UploadButton';
import NavMenu from '../../navMenu/NavMenu';
import CompetitionStatus from '../CompetitionStatus';
import DocumentCard from '../document/DocumentCard';
import DocumentTable from '../document/DocumentTable';
import UploadDocumentModal from '../document/UploadDocumentModal';
import CustomToast from '../../../components/CustomToast';

function CompetitionAmendment() {
  const [amendmentList, setAmendmentList] = useState();
  const [filteredAmendmentList, setFilteredAmendmentList] = useState();
  const [competition, setCompetition] = useState();
  const [competitionName, setCompetitionName] = useState();
  const [chosenDocument, setChosenDocument] = useState();
  const [uploadModalShow, setUploadModalShow] = useState(false);
  const [deleteDocumentModalShow, setDeleteDocumentModalShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [search, setSearch] = useState('');
  const [toastType, setToastType] = useState();
  const [toastTitle, setToastTitle] = useState();
  const [toastBody, setToastBody] = useState();
  const [showToast, setShowToast] = useState(false);
  const { id: competitionId } = useParams();
  const location = useLocation();

  useEffect(() => {
    async function fetchData() {
      if (
        location.state === null ||
        Object.keys(location?.state?.competition).length === 0
      ) {
        const data = await getCompetition(competitionId);
        if (data) {
          setCompetition(data.competition);
          setCompetitionName(data.competition.basicInformation?.name);
        }
      } else {
        setCompetition(location.state.competition);
        setCompetitionName(location.state.competition.basicInformation?.name);
      }

      const documents = await getDocuments(competitionId, 'amendment');
      if (documents) {
        setAmendmentList(documents);
        setLoading(false);
        setUpdating(false);
      }
    }

    fetchData();
  }, [uploadModalShow, deleteDocumentModalShow]);

  useEffect(() => {
    if (amendmentList) {
      filterAmendmentList(search);
    }
  }, [amendmentList, search]);

  const filterAmendmentList = (value) => {
    const fullAmendmentList = amendmentList;
    const filtered = fullAmendmentList.filter(
      (a) =>
        a.displayName?.toLowerCase().includes(value.toLowerCase()) ||
        a.fileName?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredAmendmentList(filtered);
  };

  const uploadAmendmentDocument = async (values) => {
    setUpdating(true);
    const result = await addDocument(values);
    if (result) {
      switch (result) {
        case 200:
          setToastType('success');
          setToastTitle('Dokument uppladdat');
          setToastBody('Dokumentet har laddats upp till tilläggsidan');
          break;
        default:
          setToastType('danger');
          setToastTitle('Uppladdning misslyckad');
          setToastBody('Dokumentet kunde inte laddas upp till tilläggsidan');
          break;
      }
      setUploadModalShow(false);
      setShowToast(true);
    }
  };

  const onDeleteDocument = async (id) => {
    setUpdating(true);
    const result = await deleteDocument(id);
    if (result) {
      switch (result) {
        case 200:
          setToastType('success');
          setToastTitle('Dokument raderat');
          setToastBody('Dokumentet har raderats från tilläggsidan');
          break;
        default:
          setToastType('danger');
          setToastTitle('Radering misslyckad');
          setToastBody('Dokumentet kunde inte raderas från tilläggsidan');
          break;
      }
      setDeleteDocumentModalShow(false);
      setShowToast(true);
    }
  };

  const onOpenPdf = async (fileUrl) => {
    const result = await openPdf(fileUrl);
    if (result) {
      const file = new Blob([result], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      const pdfWindow = window.open();
      pdfWindow.location.href = fileURL;
    }
  };

  const uploadBtn = (
    <>
      {updating ? (
        <>
          <Spinner
            as='span'
            animation='border'
            size='sm'
            role='status'
            aria-hidden='true'
            className='me-2'
          />
          <span className='visually-hidden'>Loading...</span>
          Ladda upp
        </>
      ) : (
        'Ladda upp'
      )}
    </>
  );

  const deleteBtn = (
    <>
      {updating ? (
        <>
          <Spinner
            as='span'
            animation='border'
            size='sm'
            role='status'
            aria-hidden='true'
            className='me-2'
          />
          <span className='visually-hidden'>Loading...</span>
          Radera dokument
        </>
      ) : (
        'Radera dokument'
      )}
    </>
  );

  return (
    <div>
      <Row>
        <Col>
          <div className='d-flex flex-row gap-4 gap-md-4 gap-lg-5'>
            <div className='d-flex align-items-center'>
              <PageTitle
                title={competitionName}
                includeLinkBack={true}
                linkBackUrl='/organizer/competitions'
                includeStatus={
                  <CompetitionStatus
                    status={competition?.status}
                    id={competitionId}
                  />
                }
              />
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <NavMenu competition={competition} />
        </Col>
      </Row>
      <Row>
        <Col className='d-flex flex-column flex-md-row justify-content-between'>
          <div style={{ flexBasis: '50%' }}>
            <h4>Tillägg</h4>
            <p className='text-secondary'>
              Ladda upp tillägg till appen. Tänk på att namnge filerna på ett
              sätt att det blir tydligt vad de innehåller för tillägg. Tillåtet
              filformat är PDF. Maximal filstorlek är 2 MB.
            </p>
          </div>
        </Col>
      </Row>
      <Row className='mt-4'>
        <Col
          md={4}
          lg={4}
          xl={3}
          xxl={2}
          className='d-flex justify-content-between'
        >
          <UploadButton
            onClick={() => setUploadModalShow(true)}
            className='w-100'
          />
        </Col>
        <Col
          md={{ offset: 4, span: 4 }}
          lg={{ offset: 4, span: 4 }}
          xl={{ offset: 5, span: 4 }}
          xxl={{ offset: 7, span: 3 }}
        >
          <Form.Control
            className='rounded-pill mt-4 mt-md-0'
            placeholder='Sök'
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        {loading ? (
          <div className='d-flex justify-content-center align-items-center mt-5'>
            <Loading size='100px' text='Laddar tillägg' />
          </div>
        ) : filteredAmendmentList && filteredAmendmentList.length > 0 ? (
          <>
            {' '}
            <Col className='d-none d-lg-flex mt-2'>
              <DocumentTable
                documentList={filteredAmendmentList}
                onDelete={() => setDeleteDocumentModalShow(true)}
                onOpen={(fileUrl) => onOpenPdf(fileUrl)}
                chosenDocument={setChosenDocument}
              />
            </Col>
            <Col className='d-flex flex-column gap-3 d-lg-none mt-4'>
              {filteredAmendmentList?.map((a, index) => {
                return (
                  <DocumentCard
                    key={index}
                    document={a}
                    onDelete={() => setDeleteDocumentModalShow(true)}
                    onOpen={(fileUrl) => onOpenPdf(fileUrl)}
                    chosenDocument={setChosenDocument}
                  />
                );
              })}
            </Col>
          </>
        ) : amendmentList.length === 0 ? (
          <EmptyState
            headingText='Inga tillägg uppladdade'
            bodyText='Ladda upp tillägg för att göra dem tillgängliga i appen.'
            action={<UploadButton onClick={() => setUploadModalShow(true)} />}
          />
        ) : (
          <EmptyState
            headingText={'Din sökning gav inga träffar'}
            bodyText={
              'Kontrollera att stavningen är rätt. Du kan söka efter visningsnamn och filnamn'
            }
          />
        )}
      </Row>

      <UploadDocumentModal
        uploadDocument={uploadAmendmentDocument}
        type={1}
        competitionId={competitionId}
        confirmBtnText={uploadBtn}
        show={uploadModalShow}
        onHide={() => setUploadModalShow(false)}
      />
      <ModalCentered
        header='Radera dokument'
        body='Permanent radera dokumentet? Du kan inte ångra den här åtgärden.'
        confirmBtnText={deleteBtn}
        onConfirm={() => onDeleteDocument(chosenDocument)}
        show={deleteDocumentModalShow}
        onHide={() => setDeleteDocumentModalShow(false)}
      />
      <CustomToast
        type={toastType}
        title={toastTitle}
        body={toastBody}
        show={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
}

export default CompetitionAmendment;
